import { readStringClaim } from "@/lib/auth/claims";
import {
  type AuthUserDisplaySnapshot,
  readDisplayNameFromIdentities,
  readDisplayNameFromUserMetadata,
} from "@/lib/auth/user-display-name";

export type DatadogAuthUserSnapshot = AuthUserDisplaySnapshot & {
  id?: string | null;
};

export type DatadogUserIdentity = {
  id: string;
  email?: string;
  name?: string;
  orgId: string | null;
};

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readRecord(record: unknown, key: string): Record<string, unknown> | null {
  if (typeof record !== "object" || record === null) return null;
  const value = (record as Record<string, unknown>)[key];
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readRecordString(record: unknown, key: string): string | null {
  if (typeof record !== "object" || record === null) return null;
  return readTrimmedString((record as Record<string, unknown>)[key]);
}

export function toDatadogRumUser(identity: DatadogUserIdentity): {
  id: string;
  email?: string;
  name?: string;
} {
  return {
    id: identity.id,
    ...(identity.email ? { email: identity.email } : {}),
    ...(identity.name ? { name: identity.name } : {}),
  };
}

export function resolveDatadogUserIdentity({
  user,
  claims,
}: {
  user: DatadogAuthUserSnapshot | null | undefined;
  claims: unknown;
}): DatadogUserIdentity | null {
  const id = readTrimmedString(user?.id) ?? readStringClaim(claims, "sub");
  if (!id) return null;

  const claimsMetadata = readRecord(claims, "user_metadata");
  const email =
    readTrimmedString(user?.email) ??
    readRecordString(user?.user_metadata, "email") ??
    readStringClaim(claims, "email") ??
    readRecordString(claimsMetadata, "email");
  const name =
    readDisplayNameFromUserMetadata(user?.user_metadata) ??
    readDisplayNameFromUserMetadata(claims as Record<string, unknown>) ??
    readDisplayNameFromUserMetadata(claimsMetadata) ??
    readDisplayNameFromIdentities(user?.identities);

  return {
    id,
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
    orgId: readStringClaim(claims, "company_id"),
  };
}
